import { useEffect, useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ChatWindow, { chatApi } from "@/components/ChatWindow";
import { api } from "@/lib/api";
import { FileText, MessageCircle, Inbox, Loader2, Star } from "lucide-react";
import AdminTestimonials from "@/components/AdminTestimonials";

const STATUS_LABEL = { nouveau: "Nouveau", en_cours: "En cours", traite: "Traité" };
const STATUS_COLOR = {
  nouveau: "bg-[#C86641] text-[#FAFAFA]",
  en_cours: "bg-amber-500 text-white",
  traite: "bg-emerald-600 text-white",
};
const NEXT_STATUS = { nouveau: "en_cours", en_cours: "traite", traite: "nouveau" };

export default function AdminDashboard() {
  const [tab, setTab] = useState("devis");
  const [quotes, setQuotes] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedConv, setSelectedConv] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadQuotes = useCallback(async () => {
    const { data } = await api.get("/quotes");
    setQuotes(data);
  }, []);

  const loadConversations = useCallback(async () => {
    const { data } = await api.get("/chat/conversations");
    setConversations(data);
  }, []);

  useEffect(() => {
    Promise.all([loadQuotes(), loadConversations()]).finally(() => setLoading(false));
    const t = setInterval(() => {
      loadQuotes();
      loadConversations();
    }, 6000);
    return () => clearInterval(t);
  }, [loadQuotes, loadConversations]);

  const cycleStatus = async (q) => {
    const next = NEXT_STATUS[q.status];
    try {
      await api.patch(`/quotes/${q.id}/status`, null, { params: { status_value: next } });
      await loadQuotes();
      setSelectedQuote((sq) => (sq && sq.id === q.id ? { ...sq, status: next } : sq));
    } catch { /* ignore */ }
  };

  return (
    <div className="bg-[#FAFAFA] text-[#1A1918] min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-28 md:pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#C86641]">Tableau de bord — Administrateur</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter mt-3" data-testid="admin-title">
            Vos demandes &amp; conversations.
          </h1>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard label="Devis reçus" value={quotes.length} accent />
            <StatCard label="En cours" value={quotes.filter((q) => q.status === "en_cours").length} />
            <StatCard label="Conversations" value={conversations.length} />
          </div>

          <div className="mt-10 flex gap-2 border-b border-[#E5E3DD] overflow-x-auto no-scrollbar">
            <TabBtn active={tab === "devis"} onClick={() => setTab("devis")} icon={<FileText size={16} />} label={`Devis (${quotes.length})`} testId="admin-tab-devis" />
            <TabBtn active={tab === "chat"} onClick={() => setTab("chat")} icon={<MessageCircle size={16} />} label={`Discussions (${conversations.length})`} testId="admin-tab-chat" />
            <TabBtn active={tab === "avis"} onClick={() => setTab("avis")} icon={<Star size={16} />} label="Avis clients" testId="admin-tab-avis" />
          </div>

          {loading ? (
            <div className="py-20 text-center text-[#5C5A56]"><Loader2 className="animate-spin inline mr-2" size={16} /> Chargement…</div>
          ) : tab === "avis" ? (
            <AdminTestimonials />
          ) : tab === "devis" ? (
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-5 border border-[#E5E3DD] bg-[#FAFAFA] max-h-[70vh] overflow-y-auto no-scrollbar">
                {quotes.length === 0 ? (
                  <div className="p-10 text-center text-[#5C5A56]"><Inbox className="mx-auto mb-3" />Aucune demande pour l'instant.</div>
                ) : (
                  quotes.map((q) => (
                    <button
                      key={q.id}
                      onClick={() => setSelectedQuote(q)}
                      data-testid={`admin-quote-row-${q.id}`}
                      className={`w-full text-left p-5 border-b border-[#E5E3DD] hover:bg-[#F3F1ED] transition-colors ${selectedQuote?.id === q.id ? "bg-[#F3F1ED]" : ""}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{q.full_name}</div>
                          <div className="text-sm text-[#5C5A56] truncate">{q.project_type}</div>
                        </div>
                        <span className={`text-[10px] px-2 py-1 font-bold tracking-wider uppercase shrink-0 ${STATUS_COLOR[q.status]}`}>
                          {STATUS_LABEL[q.status]}
                        </span>
                      </div>
                      <div className="text-xs text-[#5C5A56] mt-2">{new Date(q.created_at).toLocaleString("fr-FR")}</div>
                    </button>
                  ))
                )}
              </div>
              <div className="lg:col-span-7 border border-[#E5E3DD] bg-[#FAFAFA] p-6 min-h-[400px]">
                {selectedQuote ? (
                  <div data-testid="admin-quote-detail">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#5C5A56]">Demande de devis</div>
                        <h3 className="font-display text-3xl tracking-tight mt-2">{selectedQuote.project_type}</h3>
                      </div>
                      <button
                        onClick={() => cycleStatus(selectedQuote)}
                        data-testid={`admin-quote-status-${selectedQuote.id}`}
                        className={`text-xs px-3 py-2 font-bold tracking-wider uppercase ${STATUS_COLOR[selectedQuote.status]}`}
                      >
                        {STATUS_LABEL[selectedQuote.status]} ↻
                      </button>
                    </div>
                    <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <Info label="Nom" value={selectedQuote.full_name} />
                      <Info label="Téléphone" value={<a href={`tel:${selectedQuote.phone}`} className="text-[#C86641]">{selectedQuote.phone}</a>} />
                      <Info label="Email" value={<a href={`mailto:${selectedQuote.email}`} className="text-[#C86641] break-all">{selectedQuote.email}</a>} />
                      <Info label="Ville" value={selectedQuote.city || "—"} />
                      <Info label="Reçu le" value={new Date(selectedQuote.created_at).toLocaleString("fr-FR")} />
                    </dl>
                    <div className="mt-6">
                      <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#5C5A56] mb-2">Description du projet</div>
                      <p className="bg-[#F3F1ED] p-4 whitespace-pre-wrap text-sm leading-relaxed">{selectedQuote.description}</p>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-[#5C5A56]">
                    Sélectionnez une demande pour voir les détails.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-4 border border-[#E5E3DD] bg-[#FAFAFA] max-h-[70vh] overflow-y-auto no-scrollbar">
                {conversations.length === 0 ? (
                  <div className="p-10 text-center text-[#5C5A56]"><Inbox className="mx-auto mb-3" />Aucune conversation.</div>
                ) : (
                  conversations.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setSelectedConv(c)}
                      data-testid={`admin-conv-item-${c.id}`}
                      className={`w-full text-left p-5 border-b border-[#E5E3DD] hover:bg-[#F3F1ED] transition-colors ${selectedConv?.id === c.id ? "bg-[#F3F1ED]" : ""}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{c.client_name}</div>
                          <div className="text-xs text-[#5C5A56] truncate">{c.client_email}</div>
                          {c.last_message && (
                            <div className="text-sm text-[#5C5A56] truncate mt-2">{c.last_message}</div>
                          )}
                        </div>
                        {c.unread_for_admin > 0 && (
                          <span className="text-[10px] px-2 py-1 bg-[#C86641] text-white font-bold rounded-full">
                            {c.unread_for_admin}
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
              <div className="lg:col-span-8 h-[70vh] min-h-[500px]">
                {selectedConv ? (
                  <div className="h-full flex flex-col">
                    <div className="border border-[#E5E3DD] border-b-0 p-4 bg-[#F3F1ED]" data-testid="admin-conv-header">
                      <div className="font-semibold">{selectedConv.client_name}</div>
                      <div className="text-xs text-[#5C5A56]">{selectedConv.client_email}</div>
                    </div>
                    <div className="flex-1">
                      <ChatWindow
                        key={selectedConv.id}
                        fetchMessages={() => chatApi.conversationMessages(selectedConv.id)}
                        sendMessage={(content) => chatApi.sendAdmin(selectedConv.id, content)}
                        currentRole="admin"
                        emptyText="Première conversation avec ce client."
                        placeholder="Votre réponse au client…"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-[#5C5A56] border border-[#E5E3DD] bg-[#FAFAFA]">
                    Sélectionnez une conversation.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`border border-[#E5E3DD] p-5 ${accent ? "bg-[#1A1918] text-[#FAFAFA]" : "bg-[#FAFAFA]"}`}>
      <div className={`text-[11px] tracking-[0.2em] uppercase font-bold ${accent ? "text-[#C86641]" : "text-[#5C5A56]"}`}>{label}</div>
      <div className="font-display text-4xl tracking-tighter mt-2 font-extrabold">{value}</div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#5C5A56]">{label}</div>
      <div className="mt-1 text-[#1A1918]">{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-3 text-sm font-semibold inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${
        active ? "border-[#C86641] text-[#1A1918]" : "border-transparent text-[#5C5A56] hover:text-[#1A1918]"
      }`}
    >
      {icon} {label}
    </button>
  );
}
